/**
 *
 *  Special Offer Box on Lifestyle landing page
 *  Component
 *
 *  Created by Vladimir Shishlyannikov on 2024-08-13.
 *  Copyright (c) 2023-present iWoo Ltd. All rights reserved.
 *
 */
import React, { useCallback, useState } from "react";
import { Link } from "react-router-dom";

// Components
import TitleWithSeparator from "../../TitleWithSeparator/TitleWithSeparator";
import Button from "../../Button/Button";

// Icons
import CopyIcon from "../../../icons/CopyIcon";
import GiftBigIcon from "../../../icons/GiftBigIcon";
import ArrowRightIcon from "../../../icons/ArrowRightIcon";

// Utils
import copy from "../../../utils/copy";
import { makeCancelable } from "../../../utils";
import TBKTooltip from "../../TruBeTooltip/TruBeTooltip";
import { getBuyMembershipPath } from "../../../shared/router";

const AUTOHIDE_TIME = 3000;

const SpecialOfferBox = ({
  isUserLoggedIn,
  stateFrom,
  priceLabel,
  region,
  city,
  onCtaClick,
}) => {
  const [isCopyTooltipShow, setIsCopyTooltipShow] = useState(false);

  const copyButtonRef = React.useRef(null);
  const hideCancelablePromiseRef = React.useRef(null);

  const membershipJoinCode = "TBSEVEN"; //process.env.REACT_APP_MEMBERSHIP_JOIN_CODE;

  const handleCopyToClipboard = useCallback(
    (e) => {
      if (copy(membershipJoinCode)) {
        showCopyTooltip();
      }

      // Analytics.trackEvent(SHARE_CATEGORY, '', target.tagName.toLocaleLowerCase() === 'button' ? EVENT_SHARE_LINK : EVENT_SHARE_CLIPBOARD);
    },
    [membershipJoinCode] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const showCopyTooltip = useCallback(() => {
    if (hideCancelablePromiseRef.current) {
      return;
    }

    // Auto hide afer 4 sec
    var hidePromise = new Promise((resolve, reject) => {
      setIsCopyTooltipShow(true);

      setTimeout(() => {
        resolve();
      }, AUTOHIDE_TIME);
    });

    hideCancelablePromiseRef.current = makeCancelable(hidePromise);

    return hideCancelablePromiseRef.current.promise.then(() => {
      hideCancelablePromiseRef.current = null;
      hideCopyTooltip();
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const hideCopyTooltip = useCallback(() => {
    if (hideCancelablePromiseRef.current) {
      hideCancelablePromiseRef.current.cancel();
      hideCancelablePromiseRef.current = null;
    }

    setIsCopyTooltipShow(false);
  }, []);

  const handleCtaClick = React.useCallback(() => {
    //handleCopyToClipboard();

    onCtaClick && onCtaClick();
  }, [onCtaClick]);

  return (
    <div className="tbk-relative tbk-overflow-hidden tbk-rounded-2xl tbk-bg-basic-white/10 tbk-px-3 tbk-pb-3 tbk-pt-2 tbk-backdrop-blur-xl tbk-will-change-background md:tbk-w-124 md:tbk-px-3 lg:tbk-pb-3 lg:tbk-pt-2">
      <TitleWithSeparator
        className="tbk-mb-3 tbk-opacity-50"
        titleClass="tbk-text-blue-grey-light tbk-text-main"
      >
        Introductory Offer
      </TitleWithSeparator>

      <div className="tbk-mb-3 tbk-flex tbk-items-center tbk-justify-center md:tbk-mb-2">
        <GiftBigIcon size={48} className="tbk-mb-3_ tbk-mr-2 tbk-shrink-0" />
        <div>
          <div className="tbk-white-space-nowrap tbk-text-h3-subtitle !tbk-leading-normal tbk-text-basic-white">
            Become a member and enjoy 3 classes for{" "}
            {priceLabel ? (
              priceLabel
            ) : (
              <span className="tbk-inline-block tbk-w-2 after:tbk-animate-dots after:tbk-content-['.']" />
            )}{" "}
            per month.
          </div>

          {false && (
            <div className="!tbk-hidden tbk-items-center lg:tbk-flex">
              <div className="tbk-flex tbk-items-center tbk-justify-start md:tbk-justify-start lg:tbk-justify-center">
                <div className="tbk-text-title-medium tbk-mr-2 tbk-text-blue-grey-light tbk-line-through tbk-opacity-50">
                  {priceLabel ? (
                    priceLabel
                  ) : (
                    <span className="tbk-inline-block tbk-w-2 after:tbk-animate-dots after:tbk-content-['.']" />
                  )}
                </div>
                <svg
                  width="20"
                  height="16"
                  viewBox="0 0 20 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className="tbk-mr-2"
                >
                  <path
                    d="M1 7C0.447715 7 0 7.44772 0 8C0 8.55228 0.447715 9 1 9V7ZM19.7071 8.70711C20.0976 8.31658 20.0976 7.68342 19.7071 7.29289L13.3431 0.928932C12.9526 0.538408 12.3195 0.538408 11.9289 0.928932C11.5384 1.31946 11.5384 1.95262 11.9289 2.34315L17.5858 8L11.9289 13.6569C11.5384 14.0474 11.5384 14.6805 11.9289 15.0711C12.3195 15.4616 12.9526 15.4616 13.3431 15.0711L19.7071 8.70711ZM1 9H19V7H1V9Z"
                    fill="white"
                  />
                </svg>
                <div className="tbk-text-title-bold tbk-mr-2 tbk-text-basic-white">
                  {/*symbol*/}37
                </div>
              </div>
              <div class="tbk-text-main tbk-text-grey-inactive">
                Limited time only
              </div>
            </div>
          )}
        </div>
      </div>
      <div className="with-tooltip tbk-relative">
        <Link
          to={{
            pathname: isUserLoggedIn ? `/checkout` : `/become-a-member`,
            search: isUserLoggedIn
              ? `?membershipId=1&region=${region}&city=${city}`
              : `?return_to=${encodeURIComponent(
                  getBuyMembershipPath({ region, city })
                )}`,
            state: { from: stateFrom },
          }}
          // href="https://trubeapp.com/app-redirects/deeplink-redirect-events.html"
          // target="_blank"
        >
          <Button
            ref={copyButtonRef}
            size="md"
            variant={"secondary"}
            className="tbk-mx-auto tbk-w-72"
            // className="tbk-mt-3 tbk-mb-3 tbk-inline-flex tbk-w-80 tbk-pl-5 tbk-pr-5 lg:tbk-mt-5 lg:tbk-mb-0"
            endIcon={<ArrowRightIcon className="tbk-ml-0.5" />}
            onClick={handleCtaClick}
          >
            Join Today
          </Button>
        </Link>
        {/*<Button
          // size="md"
          variant="secondary"
          endIcon={<CopyIcon className={"tbk-text-coral"} />}
          // href="#"
          onClick={handleCtaClick}
        >
          {membershipJoinCode}
        </Button>*/}
        <TBKTooltip
          element={copyButtonRef.current}
          adjust
          inside
          placement="top"
          isShow={isCopyTooltipShow}
          onClick={hideCopyTooltip}
        >
          Copied!
        </TBKTooltip>
      </div>
    </div>
  );
};

export default SpecialOfferBox;
